import React from "react";
import { Box, Grid2 as Grid, Typography } from "@mui/material";
import {
  TitleEvidenceType,
  TITLE_EVIDENCE_TYPE_LABELS,
  TitleItem,
} from "../../../types/title";
import { formatStringArray } from "../../../common/formatters/workFormatters";
import { useEntityHandlerContext } from "../../../controllers/entityController";
import ViewAccordion, { ViewAccordionField } from "./ViewAccordion";
import ViewSubCard from "./ViewSubCard";

const displayText = (value: unknown): string =>
  value !== undefined && value !== null && value !== "" ? String(value) : "N/A";

const formatEvidenceType = (value: unknown): string =>
  value &&
  typeof value === "string" &&
  value in TITLE_EVIDENCE_TYPE_LABELS
    ? TITLE_EVIDENCE_TYPE_LABELS[value as TitleEvidenceType]
    : "N/A";

const EvidenceDetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}> = ({ label, value, fullWidth = false }) => (
  <Grid size={fullWidth ? { xs: 12 } : { xs: 12, sm: 6 }}>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box>{value}</Box>
    </Box>
  </Grid>
);

const renderEvidenceSubCard = (
  evidence: NonNullable<TitleItem["evidences"]>[number]
): React.ReactNode => (
  <ViewSubCard variant="nested">
    <EvidenceDetailRow
      label="Type"
      value={
        <Typography variant="body1">
          {formatEvidenceType(evidence?.evidence_type)}
        </Typography>
      }
    />
    <EvidenceDetailRow
      label="Subtype"
      value={
        <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
          {formatStringArray(evidence?.evidence_subtype)}
        </Typography>
      }
    />
  </ViewSubCard>
);

const getTitleFields = (titleItem: TitleItem): ViewAccordionField[] => {
  const evidences = Array.isArray(titleItem?.evidences) ? titleItem.evidences : [];
  const fields: ViewAccordionField[] = [];

  if (evidences.length === 0) {
    fields.push({
      label: "Evidences",
      value: <Typography variant="body1">N/A</Typography>,
    });
    return fields;
  }

  evidences.forEach((evidence, evidenceIndex) => {
    const evidencePrefix = `Evidence ${evidenceIndex + 1}`;

    fields.push({
      label: evidencePrefix,
      value: renderEvidenceSubCard(evidence),
    });
  });

  return fields;
};

const ViewTitlesAccordion: React.FC = () => {
  const { methods } = useEntityHandlerContext();
  const data = methods.getValues();
  const rawValue = data?.title;
  const titles: TitleItem[] = Array.isArray(rawValue) ? rawValue : [];

  if (titles.length === 0) {
    return <Typography variant="body1">N/A</Typography>;
  }

  return (
    <ViewAccordion<TitleItem>
      items={titles}
      getItemId={(item, index) => `${item?.title || "title"}-${index}`}
      getItemTitle={(item) => displayText(item?.title)}
      getItemFields={(item) => getTitleFields(item)}
      allowMultiple={false}
      unmountOnExit
      headingLevel="h5"
      pageSize={10}
    />
  );
};

export default ViewTitlesAccordion;
